import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import SessionAction from 'store/session/SessionAction'
import { Notification } from 'types/entities/Notification'
import UiNotificationsDropdownList from './UiNotificationsDropdownComponents/UiNotificationsDropdownList'
import UiNotificationDetailPanel from './UiNotificationsDropdownComponents/UiNotificationDetailPanel'

const useDropdownPositioning = (
    isOpen: boolean,
    onClose?: () => void,
    width = 320,
    margin = 8
) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const anchorRef = useRef<HTMLSpanElement>(null)
    const [position, setPosition] = useState<{
        top: number
        left: number
    } | null>(null)

    useEffect(() => {
        if (!isOpen) return

        const handlePointerDown = (e: MouseEvent) => {
            const el = containerRef.current
            if (!el) return
            if (!el.contains(e.target as Node)) {
                onClose?.()
            }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose?.()
            }
        }

        document.addEventListener('mousedown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen, onClose])

    useLayoutEffect(() => {
        if (!isOpen) return

        const compute = () => {
            const anchor = anchorRef.current
            if (!anchor) return

            const rect = anchor.getBoundingClientRect()
            const left = Math.min(
                window.innerWidth - margin - width,
                rect.right - width
            )
            const top = rect.bottom + margin

            setPosition({ top, left: Math.max(margin, left) })
        }

        compute()
        window.addEventListener('resize', compute)
        window.addEventListener('scroll', compute, true)
        return () => {
            window.removeEventListener('resize', compute)
            window.removeEventListener('scroll', compute, true)
        }
    }, [isOpen, width, margin])

    return { containerRef, anchorRef, position }
}

interface UiNotificationsDropdownProps {
    isOpen: boolean
    onClose?: () => void
}

const UiNotificationsDropdown: React.FC<UiNotificationsDropdownProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)

    const handleClose = () => {
        setSelectedNotification(null)
        onClose?.()
    }

    const { containerRef, anchorRef, position } = useDropdownPositioning(
        isOpen,
        handleClose
    )

    const notifications = useTypedSelector(
        (state) => state.sessionState.notifications
    )

    const handleMarkAllRead = () => {
        void dispatch(SessionAction.markAllNotificationsRead() as any).catch(() => undefined)
    }

    const handleMarkRead = (notification: Notification) => {
        if (!notification.read_at) {
            void dispatch(SessionAction.markNotificationRead(notification) as any).catch(() => {
                // The write can succeed before the backend fails to build its response.
                // Reload authoritative state without pretending the request succeeded.
                void dispatch(SessionAction.fetchNotifications() as any).catch(() => undefined)
            })
        }
    }

    const handleNavigate = (path: string) => {
        onClose?.()
        navigate(path)
    }

    const handleItemClick = (notification: Notification) => {
        setSelectedNotification(notification)
    }

    return (
        <>
            <span
                ref={anchorRef}
                className="UiNotificationsDropdownAnchor inline-block align-top w-0 h-0 invisible"
            />
            {isOpen &&
                position &&
                ReactDOM.createPortal(
                    <div
                        ref={containerRef}
                        style={{
                            position: 'fixed',
                            top: position.top,
                            left: position.left,
                            width: 320,
                            maxHeight: 'calc(100vh - 100px)',
                        }}
                        className={clsx(
                            'UiNotificationsDropdown flex flex-col z-[9999] rounded-xl bg-[var(--color-bg)] text-[var(--color-text)] shadow-xl ring-1 ring-black/5 dark:ring-white/10 animate-in fade-in zoom-in-95 duration-100 ease-out overflow-hidden'
                        )}
                    >
                        {selectedNotification ? (
                            <UiNotificationDetailPanel
                                notification={notifications.find(item => item.id === selectedNotification.id) ?? selectedNotification}
                                onBack={() => setSelectedNotification(null)}
                                onMarkRead={handleMarkRead}
                                onNavigate={handleNavigate}
                                onClose={handleClose}
                                t={t}
                            />
                        ) : (
                            <>
                                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] shrink-0">
                                    <span className="text-sm font-semibold uppercase tracking-wide">
                                        {t('Notifications')}
                                    </span>
                                    {notifications.length > 0 && (
                                        <button
                                            onClick={handleMarkAllRead}
                                            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors cursor-pointer font-medium hover:underline"
                                        >
                                            {t('Mark all read')}
                                        </button>
                                    )}
                                </div>

                                <div className="overflow-y-auto custom-scrollbar">
                                    <UiNotificationsDropdownList
                                        notifications={notifications}
                                        onClick={handleItemClick}
                                        translate={t}
                                    />
                                </div>
                            </>
                        )}
                    </div>,
                    document.body
                )}
        </>
    )
}

export default UiNotificationsDropdown
